import { useCallback, useEffect, useRef } from 'react';
import { driver, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import {
  COMMENTS_TOUR_SELECTORS,
  buildCommentsMovedTourSteps,
  type CommentsTourSurface,
} from '../../utils/commentsMovedTour';
import './CommentsMovedTour.css';

/**
 * Interactive "comments moved" walkthrough. Spotlights the real comments entry
 * button, then opens a representative thread to point out the Comments and PDF
 * Comments tabs. Styled in the PM brand palette. Auto-starts once per device
 * (versioned localStorage key); bump the suffix to re-show after future changes.
 */
const STORAGE_KEY = 'lctf:commentsTour:v2';

export interface UseCommentsMovedTourOptions {
  /** Gate auto-start (e.g. wait until the page has data and the user is known). */
  enabled: boolean;
  /** 'group' = Project Hub group line; 'task' = Architect/Engineer portal task row. */
  surface: CommentsTourSurface;
  /**
   * Opens a representative group thread with NO read side-effects so the popup
   * tabs can be spotlighted. Returns false when nothing is available to demo.
   */
  openDemo: () => boolean;
  /** Closes the demo thread opened by `openDemo`. */
  closeDemo: () => void;
}

function elementExists(selector: string): boolean {
  return typeof document !== 'undefined' && !!document.querySelector(selector);
}

function waitForElement(selector: string, timeoutMs = 1800): Promise<boolean> {
  return new Promise((resolve) => {
    if (elementExists(selector)) {
      resolve(true);
      return;
    }
    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      if (elementExists(selector)) {
        window.clearInterval(intervalId);
        resolve(true);
      } else if (Date.now() - startedAt > timeoutMs) {
        window.clearInterval(intervalId);
        resolve(false);
      }
    }, 60);
  });
}

export function markCommentsTourSeen(): void {
  try {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
  } catch {
    /* localStorage unavailable (private mode) — ignore */
  }
}

export function hasSeenCommentsTour(): boolean {
  try {
    return !!localStorage.getItem(STORAGE_KEY);
  } catch {
    return false;
  }
}

export function useCommentsMovedTour(options: UseCommentsMovedTourOptions) {
  const { enabled, surface, openDemo, closeDemo } = options;

  const driverRef = useRef<Driver | null>(null);
  const autoStartedRef = useRef(false);
  const openDemoRef = useRef(openDemo);
  const closeDemoRef = useRef(closeDemo);
  openDemoRef.current = openDemo;
  closeDemoRef.current = closeDemo;

  const entrySelector =
    surface === 'group' ? COMMENTS_TOUR_SELECTORS.entryGroup : COMMENTS_TOUR_SELECTORS.entryTask;

  const destroyTour = useCallback(() => {
    driverRef.current?.destroy();
    driverRef.current = null;
  }, []);

  const startTour = useCallback(() => {
    destroyTour();

    const hasEntry = elementExists(entrySelector);
    const steps = buildCommentsMovedTourSteps({ surface, hasEntry, showGroupArStrip: hasEntry });
    let demoOpened = false;

    // Open the demo thread when leaving the entry button so popup targets exist.
    const commentsTabIndex = steps.findIndex(
      (s) => s.element === COMMENTS_TOUR_SELECTORS.commentsTab,
    );
    const preTabSelector =
      commentsTabIndex > 0 && typeof steps[commentsTabIndex - 1]?.element === 'string'
        ? (steps[commentsTabIndex - 1].element as string)
        : undefined;

    const driverObj = driver({
      steps,
      showProgress: steps.length > 2,
      progressText: '{{current}} of {{total}}',
      nextBtnText: 'Next',
      prevBtnText: 'Back',
      doneBtnText: 'Got it',
      popoverClass: 'cm-tour-popover',
      smoothScroll: true,
      stagePadding: 6,
      stageRadius: 8,
      allowClose: true,
      overlayColor: '#1A1A1A',
      overlayOpacity: 0.5,
      onNextClick: (_element, step, { driver: d }) => {
        const currentSelector = typeof step?.element === 'string' ? step.element : undefined;
        if (hasEntry && currentSelector === entrySelector && !demoOpened) {
          demoOpened = openDemoRef.current();
          if (demoOpened) {
            const waitTarget =
              preTabSelector === COMMENTS_TOUR_SELECTORS.groupCommentActionRequired
                ? COMMENTS_TOUR_SELECTORS.groupCommentActionRequired
                : COMMENTS_TOUR_SELECTORS.commentsTab;
            void waitForElement(waitTarget).then(() => d.moveNext());
            return;
          }
        }
        if (d.isLastStep()) {
          d.destroy();
          return;
        }
        d.moveNext();
      },
      onPrevClick: (_element, step, { driver: d }) => {
        const currentSelector = typeof step?.element === 'string' ? step.element : undefined;
        if (
          demoOpened
          && (currentSelector === COMMENTS_TOUR_SELECTORS.commentsTab
            || currentSelector === COMMENTS_TOUR_SELECTORS.groupCommentActionRequired)
        ) {
          closeDemoRef.current();
          demoOpened = false;
        }
        d.movePrevious();
      },
      onCloseClick: (_element, _step, { driver: d }) => {
        d.destroy();
      },
      onDestroyed: () => {
        driverRef.current = null;
        if (demoOpened) {
          closeDemoRef.current();
          demoOpened = false;
        }
        markCommentsTourSeen();
      },
    });

    driverRef.current = driverObj;
    driverObj.drive();
    return true;
  }, [destroyTour, entrySelector, surface]);

  useEffect(() => () => destroyTour(), [destroyTour]);

  useEffect(() => {
    if (!enabled || autoStartedRef.current) return;
    if (hasSeenCommentsTour()) return;
    autoStartedRef.current = true;
    // Let the page settle (data + layout) before spotlighting elements.
    const timer = window.setTimeout(() => startTour(), 700);
    return () => window.clearTimeout(timer);
  }, [enabled, startTour]);

  return { startTour };
}

export default useCommentsMovedTour;
